import { useState } from "react";
import { toast } from "sonner";
import { X } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const Lvl = ({ label, value, tone = "text-zinc-200" }) => (
  <span className="inline-flex items-center gap-1.5">
    <span className="text-zinc-400">{label}</span>
    <span className={tone}>{value || "—"}</span>
  </span>
);

export const OrderCards = ({ orders, onChange, onDrop, highlightTicket }) => {
  const [busy, setBusy] = useState(null);

  const cancel = async (ticket) => {
    setBusy(ticket);
    try {
      await api.delete(`/orders/${ticket}`);
      onDrop?.(ticket);                 // gone from the list at once, frame confirms it
      toast.success(`Pending order ${ticket} cancelled`);
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="pending-orders-panel">
      <h3 className="text-sm font-heading font-semibold tracking-tight text-zinc-100">
        Pending Orders <span className="text-zinc-400 font-mono text-xs">({orders.length})</span>
      </h3>

      {orders.length === 0 && (
        <p
          className="border border-zinc-800 bg-zinc-900/40 rounded-sm px-4 py-12 text-center text-sm text-zinc-400"
          data-testid="no-pending-orders"
        >
          No pending orders waiting to trigger.
        </p>
      )}

      {orders.map((o) => {
        const isNew = highlightTicket === o.ticket;
        return (
          <div
            key={o.ticket}
            data-testid={`order-row-${o.ticket}`}
            className={`rounded-sm border bg-zinc-900/50 transition-shadow duration-500 ${
              isNew
                ? "border-emerald-400 shadow-[0_0_0_3px_rgba(16,185,129,0.35),0_0_28px_rgba(16,185,129,0.55)]"
                : "border-zinc-800"
            }`}
          >
            <div className="flex items-center gap-3 p-4 sm:p-5">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2.5 flex-wrap">
                  <span className="font-heading font-bold text-xl sm:text-2xl tracking-tight text-zinc-50">
                    {o.symbol}
                  </span>
                  <span
                    className={`px-2 py-1 rounded-sm text-xs uppercase font-bold tracking-wide ${
                      o.side === "buy"
                        ? "bg-emerald-500/25 text-emerald-200"
                        : "bg-red-500/25 text-red-200"
                    }`}
                  >
                    {(o.kind || "pending").replace("_", " ")}
                  </span>
                  <span className="font-mono text-sm text-zinc-100">{o.volume} lots</span>
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] uppercase font-semibold bg-amber-500/20 text-amber-200">
                    waiting
                  </span>
                </div>
                <div className="mt-2.5 flex items-center gap-x-5 gap-y-2 flex-wrap font-mono">
                  <span className="font-bold text-2xl sm:text-3xl tracking-tight text-zinc-100">
                    {o.price}
                  </span>
                  <div className="flex items-center gap-x-4 gap-y-1 flex-wrap text-xs sm:text-sm">
                    <Lvl label="now" value={o.price_current} />
                    <Lvl label="SL" value={o.sl} tone="text-red-300" />
                    <Lvl label="TP1" value={o.tp1} tone="text-emerald-300" />
                    <Lvl label="TP2" value={o.tp2} tone="text-emerald-300" />
                  </div>
                </div>
              </div>
              <button
                data-testid={`cancel-order-btn-${o.ticket}`}
                disabled={busy === o.ticket}
                onClick={() => cancel(o.ticket)}
                className="shrink-0 self-stretch flex flex-col items-center justify-center gap-1 border border-zinc-600 text-zinc-200 hover:bg-red-600 hover:text-white hover:border-red-600 px-4 sm:px-6 rounded-sm text-xs uppercase font-bold tracking-wide transition-colors duration-200 active:scale-95 disabled:opacity-40"
              >
                <X className="h-5 w-5" /> Cancel
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
